import {
  CaltivationCategoryOptions,
  ListingCategoryConst,
  ProductionHistoryOptions,
} from "../../../constants";
import type { BiddingInfo } from "../../../types";
import { useTruncatedTooltip } from "../../common/TruncatedTooltip";
import { formatManufacturingMethod, formatCombinedMemo, hasAnyMemo } from "../../../utils/biddingFieldFormat";

// 詳細用Props
interface BiddingItemDetailsProps {
  item: BiddingInfo;
  displayInfo: { totalCapacity: number };
}

// # 詳細コンポーネント ---
export const BiddingItemDetails: React.FC<BiddingItemDetailsProps> = ({
  item,
}) => {
  const { TooltipElement } = useTruncatedTooltip();
  // 出品区分の判定（製法区分・備考1〜3は入札会専用のため通常入札では非表示）
  const isSale = item.listingCategory === ListingCategoryConst.SALE;
  const saleBiko1 = isSale ? item.saleBiko1 : null;
  const saleBiko2 = isSale ? item.saleBiko2 : null;
  const saleBiko3 = isSale ? item.saleBiko3 : null;

  return (
    <div className="p-1 lg:p-4 border-t border-slate-200 bg-white">
      {/* PC: 6カラム, スマホ: 2カラム */}
      <div className="grid grid-cols-2 md:grid-cols-6 lg:gap-4">
        {/* 【グループA】GAP区分 / 栽培区分 / 生産履歴 / 製法区分（入札会のみ）  */}
        {/* スマホ: 2列分を使って(col-span-2)、内部を3分割/4分割(入札会のみ)して横並びにする */}
        <div className={`col-span-2 grid ${isSale ? "grid-cols-4" : "grid-cols-3"} items-center md:contents`}>
          {/* --- 1. GAP区分 --- */}
          <div className="md:col-span-1">
            <p className="hidden md:block text-base text-slate-500">GAP区分</p>
            <p className="text-base lg:text-lg lg:font-semibold ml-2.5 lg:ml-0">
              {item.gapName}
            </p>
          </div>

          {/* --- 2. 栽培区分 --- */}
          <div className="md:col-span-1">
            <p className="hidden md:block text-base text-slate-500">栽培区分</p>
            {(() => {
              const targetOption = CaltivationCategoryOptions.find(
                (opt) => opt.value === item.caltivationCategory,
              );
              return (
                <p className="text-base lg:text-lg lg:font-bold ml-2 lg:ml-0">
                  {targetOption ? (
                    targetOption.label
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </p>
              );
            })()}
          </div>

          {/* --- 3. 生産履歴 --- */}
          <div className="md:col-span-1">
            <p className="hidden md:block text-base text-slate-500">生産履歴</p>
            <div className="flex items-center">
              {(() => {
                const targetOption = ProductionHistoryOptions.find(
                  (opt) => opt.value === item.productionHistory,
                );
                return (
                  <p className="text-base lg:text-lg lg:font-bold -ml-2.5 lg:ml-0">
                    {targetOption ? (
                      targetOption.label
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </p>
                );
              })()}
            </div>
          </div>

          {/* --- 4. 製法区分（入札会専用・通常入札では非表示） --- */}
          {isSale && (
            <div className="md:col-span-1">
              <p className="hidden md:block text-base text-slate-500">製法区分</p>
              <p className="text-base lg:text-lg lg:font-bold -ml-2.5 lg:ml-0">
                {formatManufacturingMethod(item.manufacturingMethod)}
              </p>
            </div>
          )}
        </div>

        {/* 【グループB】容量① / 容量② */}
        {/* スマホ: 2列分使って(col-span-2)、flex-wrapで折り返し配置 */}
        <div className="col-span-2 flex flex-wrap items-end justify-between gap-x-4 gap-y-2 lg:mt-2 lg:pt-2 md:contents md:border-0 md:mt-0 md:pt-0">
          {/* 左側に容量などをまとめるコンテナ(スマホ用) */}
          <div className="flex flex-wrap gap-x-4 gap-y-2 md:contents">
            {/* 容量・本数 ① */}
            {Number(item.capacity1) > 0 && Number(item.quantity1) > 0 && (
              <div className="md:col-span-1">
                <p className="hidden md:block text-sm lg:text-base text-slate-500">
                  容量(kg)/本数(本)①
                </p>
                <p className="text-lg lg:font-bold ml-2.5 lg:ml-0 ">
                  {item.capacity1}
                  <span className="text-sm font-normal ml-0.5 md:hidden">
                    kg
                  </span>
                  × {item.quantity1}
                  <span className="text-sm font-normal ml-0.5 md:hidden">
                    本
                  </span>
                </p>
              </div>
            )}

            {/* 容量・本数 ② */}
            {Number(item.capacity2) > 0 && Number(item.quantity2) > 0 && (
              <div className="md:col-span-1">
                <p className="hidden md:block text-sm lg:text-base text-slate-500">
                  容量(kg)/本数(本)②
                </p>
                <p className="text-lg lg:font-bold ml-2.5 lg:ml-0">
                  {item.capacity2}
                  <span className="text-sm font-normal ml-0.5 md:hidden">
                    kg
                  </span>{" "}
                  × {item.quantity2}
                  <span className="text-sm font-normal ml-0.5 md:hidden">
                    本
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>

        {hasAnyMemo(item.biko, saleBiko1, saleBiko2, saleBiko3) && (
          <div className="col-span-2 md:col-span-6 mt-3 pt-3 border-t border-dashed border-slate-200">
            <p className="hidden md:block text-sm lg:text-base text-slate-500">
              {isSale ? "特記事項/備考1/備考2/備考3" : "備考"}
            </p>
            <p className="text-base ml-2.5 lg:ml-0 whitespace-pre-wrap text-slate-700 leading-relaxed">
              {formatCombinedMemo(item.biko, saleBiko1, saleBiko2, saleBiko3, "-")}
            </p>
          </div>
        )}
      </div>
      {TooltipElement}
    </div>
  );
};
